import React, { useState, useRef } from "react";
import {
  Alert,
  Button,
  Typography,
  Paper,
  Box,
} from "@mui/material";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";
import { useAirframeTemplateService } from "../api/airframeTemplateService";
import { useSnackbar } from "../contexts/snackbar-context";

const formatFileSize = (sizeInBytes: number) => {
  if (sizeInBytes < 1024) {
    return `${sizeInBytes} B`;
  }

  if (sizeInBytes < 1024 * 1024) {
    return `${(sizeInBytes / 1024).toFixed(1)} KB`;
  }

  return `${(sizeInBytes / (1024 * 1024)).toFixed(2)} MB`;
};

const AirframeTemplateUploadPage: React.FC = () => {
  const { showSuccess, showError } = useSnackbar();
  const { uploadAirframeTemplate } = useAirframeTemplateService();

  const [jsonFile, setJsonFile] = useState<File | null>(null);
  const [jsonContent, setJsonContent] = useState("");
  const [uploading, setUploading] = useState(false);
  const [dragActive, setDragActive] = useState(false);

  const fileInputRef = useRef<HTMLInputElement | null>(null);

  const loadFileContent = (file: File) => {
    const reader = new FileReader();

    reader.onload = (ev) => {
      const text = ev.target?.result as string;

      try {
        JSON.parse(text);
        setJsonContent(text);
        setJsonFile(file);
      } catch {
        showError("Invalid JSON file.");
        setJsonContent("");
        setJsonFile(null);
      }
    };

    reader.onerror = () => {
      showError("Failed to read file.");
    };

    reader.readAsText(file);
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".json")) {
      showError("Only JSON files are allowed.");
      return;
    }

    loadFileContent(file);
  };

  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    setDragActive(false);

    const file = e.dataTransfer.files?.[0];
    if (!file) return;

    if (!file.name.toLowerCase().endsWith(".json")) {
      showError("Only JSON files are allowed.");
      return;
    }

    loadFileContent(file);
  };

  const handleRemoveFile = () => {
    setJsonFile(null);
    setJsonContent("");

    if (fileInputRef.current) {
      fileInputRef.current.value = "";
    }
  };

  const handleUpload = async () => {
    if (!jsonContent) return;

    try {
      JSON.parse(jsonContent);
    } catch {
      showError("Invalid JSON file.");
      return;
    }

    setUploading(true);

    try {
      await uploadAirframeTemplate({ jsonContent });

      showSuccess("Template uploaded successfully!");

      setJsonFile(null);
      setJsonContent("");

      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    } catch (error) {
      const message =
        error instanceof Error && error.message.trim().length > 0
          ? error.message
          : "Template upload failed!";
      showError(message);
    } finally {
      setUploading(false);
    }
  };

  const renderPreview = () => {
    if (!jsonContent) {
      return (
        <Typography sx={{ opacity: 0.6 }}>
          Upload a JSON file to preview it here.
        </Typography>
      );
    }

    try {
      return (
        <pre
          style={{
            color: "#fff",
            fontSize: 13,
            whiteSpace: "pre-wrap",
          }}
        >
          {JSON.stringify(JSON.parse(jsonContent), null, 2)}
        </pre>
      );
    } catch {
      return <Typography color="error">Invalid JSON file.</Typography>;
    }
  };

  return (
    <Paper
      sx={{
        p: 4,
        width: "100%",
        maxWidth: 1200,
        margin: "auto",
        mt: 4,
        background: "#181818",
        borderRadius: 3,
        color: "#fff",
      }}
    >
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          alignItems: { xs: "flex-start", md: "center" },
          flexDirection: { xs: "column", md: "row" },
          gap: 2,
        }}
      >
        <Box>
          <Typography
            variant="h5"
            gutterBottom
            sx={{ color: "#FF6A00", fontWeight: 700, mb: 0.5 }}
          >
            Upload Airframe Template
          </Typography>

          <Typography sx={{ color: "#bdbdbd" }}>
            Upload JSON templates for aircraft assemblies.
          </Typography>
        </Box>
      </Box>

      <Box
        sx={{
          display: "grid",
          gridTemplateColumns: { xs: "1fr", md: "1fr 1fr" },
          gap: 3,
          mt: 2,
        }}
      >
        {/* LEFT SIDE */}
        <Box>
          <Box
            sx={{
              border: dragActive
                ? "2px dashed #FF6A00"
                : "2px dashed #333",
              borderRadius: 2,
              p: 4,
              textAlign: "center",
              background: dragActive ? "#232323" : "#222",
              cursor: "pointer",
            }}
            onDrop={handleDrop}
            onDragOver={(e) => {
              e.preventDefault();
              setDragActive(true);
            }}
            onDragLeave={(e) => {
              e.preventDefault();
              setDragActive(false);
            }}
          >
            <CloudUploadIcon sx={{ fontSize: 50, color: "#FF6A00" }} />

            <Typography sx={{ mt: 2 }}>
              Drag & drop JSON file here
            </Typography>

            <Typography sx={{ mt: 1 }}>or</Typography>

            <Button
              variant="outlined"
              component="label"
              aria-label="Airframe template file upload"
              sx={{
                mt: 2,
                color: "#FF6A00",
                borderColor: "#FF6A00",
              }}
            >
              Select JSON File
              <input
                ref={fileInputRef}
                type="file"
                accept=".json,application/json"
                hidden
                onChange={handleFileChange}
              />
            </Button>
          </Box>

          {jsonFile && (
            <Alert
              severity="info"
              sx={{
                mt: 2,
                alignItems: "flex-start",
                backgroundColor: "rgba(255, 106, 0, 0.12)",
                color: "#fff",
                border: "1px solid rgba(255, 106, 0, 0.35)",
                "& .MuiAlert-icon": {
                  color: "#FF6A00",
                },
              }}
              action={
                <Button size="small" color="inherit" onClick={handleRemoveFile}>
                  Remove
                </Button>
              }
            >
              <Typography sx={{ fontWeight: 700, mb: 1 }}>
                Uploaded file info
              </Typography>
              <Typography sx={{ mb: 0.5 }}>
                File name: {jsonFile.name}
              </Typography>
              <Typography>
                Size: {formatFileSize(jsonFile.size)}
              </Typography>
            </Alert>
          )}
        </Box>

        {/* RIGHT SIDE */}
        <Box>
          <Typography
            sx={{ mb: 1, fontWeight: 600, color: "#FF6A00" }}
          >
            Template Preview
          </Typography>

          <Paper
            sx={{
              background: "#111",
              p: 2,
              height: 420,
              overflow: "auto",
              border: "1px solid #333",
            }}
          >
            {renderPreview()}
          </Paper>
        </Box>
      </Box>

      {/* UPLOAD BUTTON */}
      <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
        <Button
          variant="contained"
          disabled={uploading || !jsonContent}
          onClick={handleUpload}
          sx={{
            minWidth: 200,
            background: "#FF6A00",
            "&:hover": { background: "#e65c00" },
          }}
        >
          {uploading ? "Uploading..." : "Upload Template"}
        </Button>
      </Box>
    </Paper>
  );
};

export default AirframeTemplateUploadPage;